import { appConfiguration } from "./appConfiguration.ts";

// Basic nested validation with and/or/not, expressable as JSON.

// Examples of ValidatorSteps:

// Value must not be blank:
//  { type: "IsNotBlank" }

// Value must be an int above 5:
//  { and: [
//    { type: "IsInt" },
//    { type: "Above", arguments: [5] } ]
//  }

// Value must be either an int above 5 or the string "auto":
//  { or: [
//    { and: [
//      { type: "IsInt" },
//      { type: "Above", arguments: [5] } ]
//    },
//    { type: "IsAuto" } ]
//  }

// Value must not be between 5 and 10, inclusive:
// { type: "AtOrBetween", arguments: [5, 10], not: true }

// Each item in the comma-separated list must be an int above 5:
//  { type: "CommaList",
//    and: [
//      { type: "IsInt" },
//      { type: "Above", arguments: [5] } ]
//  }


enum ValidatorType {
  // 0 arguments:
  Grouping = "Grouping",
  IsInt = "IsInt",
  IsNumber = "IsNumber",
  IsAuto = "IsAuto",
  IsNotBlank = "IsNotBlank",
  IsStrictAlphaNumeric = "IsStrictAlphaNumeric",
  CommaList = "CommaList",  // Each item in the list will be tested on all the and/or ValidatorSteps within.

  // 1 argument:
  Above = "Above",
  AtOrAbove = "AtOrAbove",
  AtOrBelow = "AtOrBelow",
  ListSumsTo = "ListSumsTo",  // Comma-separated list of numbers must sum to the provided value.

  // 2 arguments:
  AtOrBetween = "AtOrBetween"
}

interface ValidatorInfo {
  sortOrder: number;
  arguments: number;
  displayName: string;
  description: string;
}

const validatorDisplayOrder = [
  ValidatorType.Grouping,
  ValidatorType.IsInt,
  ValidatorType.IsNumber,
  ValidatorType.IsAuto,
  ValidatorType.IsNotBlank,
  ValidatorType.IsStrictAlphaNumeric,
  ValidatorType.CommaList,
  ValidatorType.Above,
  ValidatorType.AtOrAbove,
  ValidatorType.AtOrBelow,
  ValidatorType.AtOrBetween,
  ValidatorType.ListSumsTo
]

const validatorInfo: { [key in ValidatorType]: ValidatorInfo } = {
  [ValidatorType.Grouping]: {
    sortOrder: 0,
    arguments: 0,
    displayName: "Grouping",
    description: "Used for grouping and/or/not steps together.",
  },
  [ValidatorType.IsInt]: {
    sortOrder: 1,
    arguments: 0,
    displayName: "Is Integer",
    description: "Value must be an integer.",
  },
  [ValidatorType.IsNumber]: {
    sortOrder: 2,
    arguments: 0,
    displayName: "Is Number",
    description: "Value must be a number (integer or decimal).",
  },
  [ValidatorType.IsAuto]: {
    sortOrder: 3,
    arguments: 0,
    displayName: "Is 'auto'",
    description: "Value must be the string 'auto'.",
  },
  [ValidatorType.IsNotBlank]: {
    sortOrder: 4,
    arguments: 0,
    displayName: "Is Not Blank",
    description: "Value must not be blank.",
  },
  [ValidatorType.IsStrictAlphaNumeric]: {
    sortOrder: 5,
    arguments: 0,
    displayName: "Is Strict Alphanumeric",
    description: "Value must be strictly alphanumeric (letters, numbers, hyphens, and underscores only).",
  },
  [ValidatorType.CommaList]: {
    sortOrder: 6,
    arguments: 0,
    displayName: "Comma List",
    description: "Each item in the comma-separated list will be tested against the specified validators.",
  },

  [ValidatorType.Above]: {
    sortOrder: 7,
    arguments: 1,
    displayName: "Above",
    description: "Value must be a number above the specified value.",
  },
  [ValidatorType.AtOrAbove]: {
    sortOrder: 8,
    arguments: 1,
    displayName: "At Or Above",
    description: "Value must be a number at or above the specified value.",
  },
  [ValidatorType.AtOrBelow]: {
    sortOrder: 9,
    arguments: 1,
    displayName: "At Or Below",
    description: "Value must be a number at or below the specified value.",
  },
  [ValidatorType.AtOrBetween]: {
    sortOrder: 10,
    arguments: 2,
    displayName: "At Or Between",
    description: "Value must be a number between the two specified values, inclusive.",
  },
  [ValidatorType.ListSumsTo]: {
    sortOrder: 11,
    arguments: 1,
    displayName: "List Sums To",
    description: "The numbers in the comma-separated list must sum to the specified value.",
  },
};


interface ValidatorStep {
  type?: ValidatorType; // If no type is provided, this is just a wrapper for the and/or/not structure.
  arguments?: any[];
  and?: ValidatorStep[];
  or?: ValidatorStep[];
  not?: boolean;
}


function validateValueWithValidatorStep(v: string, step: ValidatorStep): boolean {

  let result: boolean =  true;
  let isGroupingType = false;
  let subValuesToTest = [v];

  if (step.type) {
    switch (step.type) {
      case ValidatorType.IsInt:
        // This will actually allow stuff like "2,244 23 23", which will parse to "2".
        // Not sure if this is a good idea.
        result = !isNaN(parseInt(v, 10));
        break;
      case ValidatorType.IsNumber:
        result = !isNaN(v as any);
        break;
      case ValidatorType.IsAuto:
        result = v.trim().toLowerCase() == "auto";
        break;
      case ValidatorType.IsNotBlank:
        result = v.trim().length > 0;
        break;
      case ValidatorType.Above:
        const nAbove = parseFloat(v);
        result = !isNaN(nAbove) && (nAbove > step.arguments![0]);
        break;
      case ValidatorType.AtOrAbove:
        const nAtOrAbove = parseFloat(v);
        result = !isNaN(nAtOrAbove) && (nAtOrAbove >= step.arguments![0]);
        break;
      case ValidatorType.AtOrBelow:
        const nAtOrBelow = parseFloat(v);
        result = !isNaN(nAtOrBelow) && (nAtOrBelow <= step.arguments![0]);
        break;
      case ValidatorType.AtOrBetween:
        const nAtOrBetween = parseFloat(v);
        result = ( !isNaN(nAtOrBetween) && (nAtOrBetween >= step.arguments![0]) && (nAtOrBetween <= step.arguments![1]) );
        break;
      case ValidatorType.CommaList:
        // Special case: Send every item in the list down to the and/or/not evaluators within.
        subValuesToTest = v.split(",");
        isGroupingType = true;
        break;
      case ValidatorType.ListSumsTo:
        result = v.split(",").map(parseFloat).reduce((acc, cur) => acc+cur, 0) == step.arguments![0];
        break;
      case ValidatorType.IsStrictAlphaNumeric:
        result = v.search(/[^A-Za-z0-9\-_]/g) < 0;
        break;
      default:
        isGroupingType = true;
    }
  } else {
    // The grouping types don't have a type attribute.
    isGroupingType = true;
  }

  if (isGroupingType) {
    // If this is a grouping term, we don't want to apply the and/or/not logic at the end to the whole value, but rather to each item in the comma-separated list.
    // So we return early here after validating the type (if any) and then applying the and/or/not logic within each item in the list.
    if (step.and && step.and.length > 0) {
      result = subValuesToTest.every((value) => step.and!.every((s) => validateValueWithValidatorStep(value, s)));
    } else if (step.or && step.or.length > 0) {
      result = subValuesToTest.every((value) => step.or!.some((s) => validateValueWithValidatorStep(value, s)));
    }
  }
  if (step.not) { result = !result; }
  return result;
}

function validatorStepToDescription(step: ValidatorStep): string {
  return validatorStepToDescriptionRecurse(step, true);
}


function validatorStepToDescriptionRecurse(step: ValidatorStep, atTop: boolean): string {

  let description = null;
  let many = false;

  if (step.type) {
    switch (step.type) {
      case ValidatorType.IsInt:
        description = "an integer";
        break;
      case ValidatorType.IsNumber:
        description = "a number";
        break;
      case ValidatorType.IsAuto:
        description = "'auto'";
        break;
      case ValidatorType.IsNotBlank:
        description = "not blank";
        break;
      case ValidatorType.Above:
        description = `above ${step.arguments![0]}`;
        break;
      case ValidatorType.AtOrAbove:
        description = `at or above ${step.arguments![0]}`;
        break;
      case ValidatorType.AtOrBelow:
        description = `at or below ${step.arguments![0]}`;
        break;
      case ValidatorType.AtOrBetween:
        description = `between ${step.arguments![0]} and ${step.arguments![1]}`;
        break;
      case ValidatorType.CommaList:
        // Special case: Send every item in the list down to the and/or/not evaluators within.
        description = "a comma-separated list";
        many = true;
        break;
      case ValidatorType.ListSumsTo:
        description = `a comma-separated list that sums to ${step.arguments![0]}`;
        break;
      case ValidatorType.IsStrictAlphaNumeric:
        description = "strictly alphanumeric";
        break;
      default:
        description = null;
    }
  }

  const hasAnd = Array.isArray(step.and) && step.and.length > 0;
  const hasOr = Array.isArray(step.or) && step.or.length > 0;

  let andDescription = null;
  if (hasAnd) {
    andDescription = step.and!.map((s) => validatorStepToDescriptionRecurse(s, false)).join(", and ");
    if (step.and!.length > 1) {
      andDescription = '(' + andDescription + ')';
    }
  }
  let orDescription = null;
  if (hasOr) {
    orDescription = step.or!.map((s) => validatorStepToDescriptionRecurse(s, false)).join(", or ");
    if (step.or!.length > 1) {
      orDescription = '(' + orDescription + ')';
    }
  }

  let fullDescription = atTop ? "Must be " : "";
  if (description) {
    if (step.not) {
      fullDescription += "not ";
    }
    fullDescription += description;
    if (hasAnd || hasOr) {
      fullDescription += many ? ", where each item is " : ", and ";
    }
  }

  if (hasAnd) {
    fullDescription += andDescription;
    if (hasOr) {
      fullDescription += ", and ";
    }
  }
  if (hasOr) {
    if (step.or!.length == 2) {
      fullDescription += "either " + orDescription;
    } else {
      fullDescription += "any of " + orDescription;
    }
  }
  return fullDescription;
}


export type { ValidatorStep }
export { ValidatorType, validatorInfo, validatorDisplayOrder, validateValueWithValidatorStep, validatorStepToDescription }